import { useCallback } from 'react';
import { Direction_Enum, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Fax, ListFaxesRequest } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { Bool_Enum } from '@weave/schema-gen-ts/dist/shared/null/types.pb';
import { useQueryClient } from 'react-query';
import { FaxQueries } from '@frontend/api-fax';
import { formatPhoneNumberWithoutCountryCode } from '@frontend/phone-numbers';
import { SchemaFaxService } from '@frontend/schema';
import { useFaxV1QueryUpdaters } from './use-query-updaters';

type addUpdateCacheParams = {
  faxData?: Fax;
  faxId?: string;
  locationId?: string;
};

export function useListFaxQueryUpdater() {
  const { updateQuery, getQueriesData, invalidateQueries } = useFaxV1QueryUpdaters();
  const queryClient = useQueryClient();

  const getFaxById = useCallback((faxId: string, locationId: string) => {
    return queryClient.fetchQuery({
      queryKey: FaxQueries.queryKeys.getFaxForUpdate(faxId),
      queryFn: () =>
        SchemaFaxService.GetFax({
          faxId,
          locationId,
        }),
      cacheTime: 0,
      staleTime: 0,
      retry: 1,
    });
  }, []);

  const isMatchingQuery = useCallback((queryRequest: Partial<ListFaxesRequest>, faxData: Fax) => {
    if (!faxData?.id || !queryRequest) return false;

    // Location matching - must match either locationId OR be included in locationIds
    const matchesLocation = queryRequest.locationId === faxData.locationId;
    const matchesLocationIds = !queryRequest.locationIds || queryRequest.locationIds.includes(faxData.locationId);

    // Location is required - early exit if no match
    if (!matchesLocation && !matchesLocationIds) {
      return false;
    }

    // Viewed status matching - A fax is "viewed" if it has viewedAt timestamp
    const isFaxViewed = !!faxData.viewedAt && faxData.direction === Direction_Enum.INBOUND;

    // Special case: If query specifically asks for non-viewed faxes and this fax is not viewed,
    // bypass all other filters and return true (only location must match)
    const matchesIsNotViewed = queryRequest.isViewed === Bool_Enum.FALSE && !isFaxViewed;
    if (matchesIsNotViewed) {
      return true;
    }

    // For all other cases, apply standard filter logic
    const matchesIsViewed =
      queryRequest.isViewed === undefined || (queryRequest.isViewed === Bool_Enum.TRUE && isFaxViewed);

    // Direction matching - undefined means no filter applied
    const matchesDirection = queryRequest.direction === undefined || queryRequest.direction === faxData.direction;

    // Tags matching - fax must have at least one matching tag if tags filter is applied
    const matchesTags =
      !queryRequest.tags ||
      queryRequest.tags.length === 0 ||
      (faxData.tags && faxData.tags.length > 0 && faxData.tags.some((tag) => queryRequest.tags?.includes(tag)));

    // Location phone numbers matching - fax must match one of the filtered fax numbers
    const matchesLocationPhones =
      !queryRequest.locationPhones ||
      queryRequest.locationPhones.length === 0 ||
      queryRequest.locationPhones.includes(faxData.locationPhone);

    // Delivery status matching - comprehensive logic
    const matchesIsDelivered = (() => {
      // No delivery filter = match all
      if (queryRequest.isDelivered === undefined) {
        return true;
      }

      // For inbound faxes, delivery status doesn't apply
      if (faxData.direction === Direction_Enum.INBOUND) {
        // Only match if query doesn't specifically require delivery status
        return queryRequest.isDelivered === Bool_Enum.FALSE;
      }

      // For outbound faxes, determine actual delivery status
      const isActuallyDelivered = faxData.status === Status_Enum.DELIVERED;
      const isActuallyFailed = [Status_Enum.FAILED_FATAL, Status_Enum.FAILED_RETRYABLE].includes(faxData.status);

      if (queryRequest.isDelivered === Bool_Enum.TRUE) {
        return isActuallyDelivered;
      }

      if (queryRequest.isDelivered === Bool_Enum.FALSE) {
        return isActuallyFailed;
      }

      return false;
    })();

    // All filters must match (AND logic)
    return matchesDirection && matchesIsViewed && matchesTags && matchesLocationPhones && matchesIsDelivered;
  }, []);

  const isUnMatchedQuery = useCallback((queryRequest: Partial<ListFaxesRequest>, faxData: Fax) => {
    if (!faxData?.id || !queryRequest) return false;

    // Location matching - must match either locationId OR be included in locationIds
    const matchesLocation = queryRequest.locationId === faxData.locationId;
    const matchesLocationIds = !queryRequest.locationIds || queryRequest.locationIds.includes(faxData.locationId);

    // Location is required - early exit if no match
    if (!matchesLocation && !matchesLocationIds) {
      return false;
    }

    // Check if fax no longer matches the viewed status filter
    const unMatchesIsViewed =
      queryRequest.isViewed !== undefined &&
      ((queryRequest.isViewed === Bool_Enum.TRUE &&
        !faxData.viewedAt &&
        faxData.direction === Direction_Enum.INBOUND) ||
        (queryRequest.isViewed === Bool_Enum.FALSE &&
          !!faxData.viewedAt &&
          faxData.direction === Direction_Enum.INBOUND));

    // Check if fax no longer matches the direction filter
    const unMatchesDirection = queryRequest.direction !== undefined && queryRequest.direction !== faxData.direction;

    // Check if fax no longer matches the delivery status filter
    const unMatchesIsDelivered =
      queryRequest.isDelivered !== undefined &&
      ((queryRequest.isDelivered === Bool_Enum.TRUE && faxData.status !== Status_Enum.DELIVERED) ||
        (queryRequest.isDelivered === Bool_Enum.FALSE && faxData.status === Status_Enum.DELIVERED));

    // Check if fax no longer matches the location phones filter
    const unMatchesLocationPhones = !!(
      queryRequest.locationPhones &&
      queryRequest.locationPhones.length > 0 &&
      !queryRequest.locationPhones.includes(faxData.locationPhone)
    );

    // Check if fax no longer matches the tags filter
    const unMatchesTags = !!(
      queryRequest.tags &&
      queryRequest.tags.length > 0 &&
      !queryRequest.tags.some((tag) => faxData?.tags?.includes(tag))
    );

    // Return true if any filter no longer matches (OR logic for removal conditions)
    return unMatchesIsViewed || unMatchesDirection || unMatchesIsDelivered || unMatchesLocationPhones || unMatchesTags;
  }, []);

  const addToListFaxCache = useCallback(
    async ({ faxData, faxId, locationId }: addUpdateCacheParams) => {
      let faxToAdd = faxData;
      try {
        if (!faxToAdd) {
          if (faxId && locationId) {
            faxToAdd = await getFaxById(faxId, locationId);
          } else {
            throw new Error('Fax ID and location ID must be provided to add to cache.');
          }
        }
      } catch (error) {
        invalidateListFaxes();
        return;
      }

      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey }) => {
            const request = queryKey[2];
            return isMatchingQuery(request, faxToAdd);
          },
        },
        updater: (data) => {
          if (!data || !data.pages) {
            return {
              pages: [{ rows: [faxToAdd] }],
              pageParams: [],
            };
          }
          return {
            ...data,
            pages: data.pages.map((page, i) => (i === 0 ? { ...page, rows: [faxToAdd, ...(page.rows || [])] } : page)),
          };
        },
      });
    },
    [updateQuery, getQueriesData]
  );

  const updateFaxInListFaxCache = useCallback(
    async ({ faxData, faxId, locationId }: addUpdateCacheParams) => {
      let faxToUpdate = faxData;
      try {
        if (!faxToUpdate) {
          if (faxId && locationId) {
            faxToUpdate = await getFaxById(faxId, locationId);
          } else {
            throw Error;
          }
        }
      } catch (error) {
        invalidateListFaxes();
        return;
      }

      // update faxData in query caches with the fax existing already.
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey, state }) => {
            const request = queryKey[2];
            const matchesLocation = request.locationId === faxToUpdate?.locationId;
            const matchesLocationIds = !request.locationIds || request.locationIds.includes(faxToUpdate?.locationId);

            const faxExists = !!state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxToUpdate?.id));

            return (matchesLocation || matchesLocationIds) && faxExists;
          },
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.map((fax) => (fax.id === faxToUpdate?.id ? faxToUpdate : fax)),
          })),
        }),
      });

      // invalidate queries cache with filters that matches the updated faxData but doesn't have the fax in them.
      invalidateQueries<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey, state }) => {
            const request = queryKey[2];
            const faxNotExists = !state.data?.pages.some((page) =>
              page.rows?.some((fax) => fax.id === faxToUpdate?.id)
            );
            return faxNotExists && isMatchingQuery(request, faxToUpdate);
          },
        },
      });

      // remove fax from queries cache with filters that doesn't match the updated faxData anymore.
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ queryKey, state }) => {
            const request = queryKey[2];
            const faxExists = !!state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxToUpdate?.id));
            return faxExists && isUnMatchedQuery(request, faxToUpdate);
          },
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.filter((fax) => fax.id !== faxToUpdate?.id) ?? [],
          })),
        }),
      });
    },
    [updateQuery, getQueriesData]
  );

  const removeFromListFaxCache = useCallback(
    (faxId: string) => {
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ state }) => {
            return state.data?.pages.some((page) => page.rows?.some((fax) => fax.id === faxId)) ?? false;
          },
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.filter((fax) => fax.id !== faxId) ?? [],
          })),
        }),
      });
    },
    [updateQuery, getQueriesData]
  );

  const removeFaxWithContactFromListFaxCache = useCallback(
    (faxNumber: string) => {
      const formattedNumber = formatPhoneNumberWithoutCountryCode(faxNumber);
      updateQuery<'ListFaxes', true>({
        endpointName: 'ListFaxes',
        queryFilters: {
          predicate: ({ state }) => {
            return (
              state.data?.pages.some((page) => page.rows?.some((fax) => fax.contactPhone === formattedNumber)) ?? false
            );
          },
        },
        updater: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            rows: page.rows?.filter((fax) => fax.contactPhone !== formattedNumber) ?? [],
          })),
        }),
      });
    },
    [updateQuery, getQueriesData]
  );

  const invalidateListFaxes = useCallback(() => {
    invalidateQueries({
      endpointName: 'ListFaxes',
    });
  }, [invalidateQueries]);

  const invalidateListArchivedFaxes = useCallback(() => {
    invalidateQueries({
      endpointName: 'ListArchivedFaxes',
    });
  }, [invalidateQueries]);

  const invalidateListBlockedFaxes = useCallback(() => {
    invalidateQueries({
      endpointName: 'ListBlockedFaxes',
    });
  }, [invalidateQueries]);

  return {
    addToListFaxCache,
    updateFaxInListFaxCache,
    removeFromListFaxCache,
    removeFaxWithContactFromListFaxCache,
    invalidateListFaxes,
    invalidateListArchivedFaxes,
    invalidateListBlockedFaxes,
  };
}
